import React, { useEffect, useState } from 'react';
import FilePreview from './FilePreview';
import {
  fetchVendorDocuments,
  VendorDocumentRow,
} from '../../services/vendorDocumentsService';

const formatDate = (value: unknown): string => {
  if (!value) return '';
  const date = new Date(String(value));
  if (Number.isNaN(date.getTime())) return '';
  return date.toLocaleDateString();
};

type Props = {
  vendorId: string;
  vendorType: number;
  className?: string;
};

const VendorDocumentsGrid: React.FC<Props> = ({ vendorId, vendorType, className }) => {
  const [docs, setDocs] = useState<VendorDocumentRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        setLoading(true);
        setError(null);
        const rows = await fetchVendorDocuments(vendorId, vendorType);
        if (!cancelled) setDocs(rows);
      } catch {
        if (!cancelled) setError('Could not load vendor documents.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, [vendorId, vendorType]);

  if (loading) {
    return (
      <div className={className}>
        <div className="h-8 w-40 animate-pulse rounded bg-gray-200 dark:bg-gray-700 mb-4" />
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {[1, 2, 3].map((k) => (
            <div key={k} className="h-36 rounded-lg border border-gray-100 dark:border-gray-700 bg-gray-50 dark:bg-gray-900/40 animate-pulse" />
          ))}
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className={className}>
        <p className="text-sm text-red-600 dark:text-red-400">{error}</p>
      </div>
    );
  }

  if (docs.length === 0) {
    return (
      <div className={className}>
        <p className="text-sm text-gray-500 dark:text-gray-400">No vendor documents on file.</p>
      </div>
    );
  }

  return (
    <div className={className}>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {docs.map((doc) => {
          const label = doc.documentName?.trim() || doc.documentType || 'Document';
          const issued = formatDate(doc.issuedDate);
          const numberLine = doc.documentNumber?.trim();
          const expiryDisplay = doc.isLifetime
            ? 'Lifetime'
            : formatDate(doc.expiryDate) || '';

          return (
            <div
              key={doc.documentId}
              className="rounded-lg border border-gray-200 dark:border-gray-600 bg-gray-50/80 dark:bg-gray-900/30 p-3 flex flex-col gap-2 min-w-0"
            >
              <div>
                <p className="text-sm font-semibold text-gray-900 dark:text-white leading-tight">{label}</p>
                {doc.documentType && doc.documentType !== doc.documentName && (
                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">{doc.documentType}</p>
                )}
              </div>
              <FilePreview url={doc.documentUrl} name={label} className="w-full flex-1 min-h-0" />
              <div className="text-xs text-gray-600 dark:text-gray-400 space-y-0.5">
                {numberLine && (
                  <p>
                    <span className="text-gray-500 dark:text-gray-500">No. </span>
                    {numberLine}
                  </p>
                )}
                {issued && (
                  <p>
                    <span className="text-gray-500 dark:text-gray-500">Issued: </span>
                    {issued}
                  </p>
                )}
                {(doc.isLifetime || expiryDisplay) && (
                  <p>
                    <span className="text-gray-500 dark:text-gray-500">Expiry: </span>
                    {doc.isLifetime ? 'Lifetime' : expiryDisplay || '—'}
                  </p>
                )}
                {doc.status && (
                  <span
                    className={`inline-block mt-1 px-2 py-0.5 rounded text-[10px] font-semibold uppercase ${
                      doc.status === 'approved'
                        ? 'bg-green-100 dark:bg-green-900/40 text-green-800 dark:text-green-300'
                        : doc.status === 'rejected'
                        ? 'bg-red-100 dark:bg-red-900/40 text-red-800 dark:text-red-300'
                        : 'bg-amber-100 dark:bg-amber-900/40 text-amber-800 dark:text-amber-300'
                    }`}
                  >
                    {doc.status}
                  </span>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default VendorDocumentsGrid;
